// Console admin des tâches (WKF-013 §7.6) — query de liste keyset (tasks:manage_all).

import { useQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'

import { buildQuery, entityHeaders, readError, TASKS_BASE } from './useNotifications.shared'
import type { AdminTask, Notification } from './useNotifications.types'

export interface AdminTaskFilters {
    user_id?: string
    status?: string
    period?: string
    linked_entity_type?: string
    /** Plage d'échéance (ISO) — période à deux bornes. */
    due_from?: string
    due_to?: string
    /** Recherche plein texte (titre + corps). */
    search?: string
    cursor?: string | null
    limit?: number
}

export interface AdminTaskList {
    items: AdminTask[]
    next_cursor: string | null
    prev_cursor: string | null
    has_more: boolean
    total_estimate: number
}

/** Récupère TOUS les ids correspondant aux filtres (across-pages, plafonné backend). */
export async function fetchAdminTaskIds(
    entityId: string,
    filters: AdminTaskFilters = {},
): Promise<{ ids: string[]; total: number; capped: boolean }> {
    const qs = buildQuery({
        user_id: filters.user_id,
        status: filters.status,
        period: filters.period,
        linked_entity_type: filters.linked_entity_type,
        due_from: filters.due_from,
        due_to: filters.due_to,
        search: filters.search,
    })
    const resp = await apiFetch(`${TASKS_BASE}/admin/ids${qs}`, {
        headers: entityHeaders(entityId),
    })
    if (!resp.ok) throw await readError(resp)
    return resp.json()
}

/** Détail d'une tâche pour la console admin (lecture seule, scope entité). */
export function useAdminTaskDetail(entityId: string | null, notificationId: string | null) {
    return useQuery<Notification>({
        queryKey: [...['tasks'], 'admin-detail', notificationId ?? ''] as const,
        enabled: Boolean(entityId) && Boolean(notificationId),
        queryFn: async () => {
            const resp = await apiFetch(`${TASKS_BASE}/admin/${notificationId}`, {
                headers: entityHeaders(entityId as string),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
}

export function useAdminTasks(entityId: string | null, filters: AdminTaskFilters = {}) {
    return useQuery<AdminTaskList>({
        queryKey: queryKeys.tasks.admin(entityId ?? '', filters as Record<string, unknown>),
        enabled: Boolean(entityId),
        staleTime: 15_000,
        queryFn: async () => {
            const qs = buildQuery({
                user_id: filters.user_id,
                status: filters.status,
                period: filters.period,
                linked_entity_type: filters.linked_entity_type,
                // Filtres recherche + plage d'échéance : DOIVENT être transmis, sinon la barre
                // de recherche et les date-pickers admin sont morts, et « sélectionner toutes
                // les correspondantes » (fetchAdminTaskIds, qui LES envoie) vise un périmètre
                // différent de la liste affichée (compteur divergent).
                due_from: filters.due_from,
                due_to: filters.due_to,
                search: filters.search,
                cursor: filters.cursor ?? undefined,
                limit: filters.limit,
            })
            const resp = await apiFetch(`${TASKS_BASE}/admin${qs}`, {
                headers: entityHeaders(entityId as string),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
}
